import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    Phone,
    PhoneCall,
    Send,
    Mic,
    MicOff,
    Copy,
    Check,
    Loader2,
    DollarSign,
    Brain,
    AlertTriangle,
    TrendingUp,
    Target,
    MessageSquare,
    Zap,
    ThumbsUp,
    ThumbsDown,
    ArrowUp,
    History,
    Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Suggestion type styles
const suggestionStyles = {
    counter_offer: { icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' },
    talking_point: { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
    warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
    insight: { icon: TrendingUp, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
    action: { icon: Target, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/30' }
};

// Copy to clipboard helper
function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            toast.error('Failed to copy');
        }
    };
    
    return (
        <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            className="h-7 px-2 text-zinc-400 hover:text-white"
        >
            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </Button>
    );
}

// Suggestion card
function SuggestionCard({ suggestion }) {
    const style = suggestionStyles[suggestion.type] || suggestionStyles.action;
    const Icon = style.icon;
    
    return (
        <div className={`p-3 rounded-lg border ${style.bg} flex items-start gap-3`}>
            <Icon className={`w-5 h-5 ${style.color} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm">{suggestion.text}</p>
                {suggestion.reason && (
                    <p className="text-zinc-400 text-xs mt-1">{suggestion.reason}</p>
                )}
            </div>
            <CopyButton text={suggestion.text} />
        </div>
    );
}

// Quick action buttons
function QuickActions({ onAction, loading }) {
    const actions = [
        { key: 'counter', label: 'Send Counter', icon: DollarSign, input: 'I want to counter with a higher rate' },
        { key: 'too_low', label: 'Too Low', icon: ThumbsDown, input: 'They said that rate is too low' },
        { key: 'accept', label: 'Accept', icon: ThumbsUp, input: 'Should I accept this offer?' },
        { key: 'push', label: 'Push Higher', icon: ArrowUp, input: 'How can I push for a higher rate?' }
    ];
    
    return (
        <div className="grid grid-cols-4 gap-2">
            {actions.map(action => {
                const Icon = action.icon;
                return (
                    <Button
                        key={action.key}
                        size="sm"
                        variant="outline"
                        onClick={() => onAction(action.input)}
                        disabled={loading}
                        className="border-zinc-700 hover:bg-zinc-800 flex flex-col items-center gap-1 h-auto py-2"
                        data-testid={`quick-action-${action.key}`}
                    >
                        <Icon className="w-4 h-4" />
                        <span className="text-xs">{action.label}</span>
                    </Button>
                );
            })}
        </div>
    );
}

// Main Call Assist Panel
export default function CallAssistPanel({ loadId, onClose }) {
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [userInput, setUserInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [quickResponse, setQuickResponse] = useState('');
    const [conversationHistory, setConversationHistory] = useState([]);
    const inputRef = useRef(null);
    
    // Fetch initial context
    const fetchContext = useCallback(async () => {
        if (!loadId) return;
        
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/call/${loadId}/context`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setContext(data);
                // Set initial suggestions
                if (data.ready_suggestions) {
                    setSuggestions(data.ready_suggestions);
                }
            } else {
                toast.error('Failed to load call context');
            }
        } catch (e) {
            console.error('Failed to fetch call context:', e);
            toast.error('Connection error');
        } finally {
            setLoading(false);
        }
    }, [loadId]);
    
    useEffect(() => {
        fetchContext();
    }, [fetchContext]);
    
    // Send message for assistance
    const sendMessage = async (input) => {
        if (!input.trim() || !loadId) return;
        
        setSending(true);
        const message = input.trim();
        setUserInput('');
        
        // Add to history
        setConversationHistory(prev => [...prev, { role: 'user', text: message }]);
        
        try {
            const res = await fetch(`${API_URL}/api/operations/call/${loadId}/assist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_input: message, include_context: true })
            });
            
            if (res.ok) {
                const data = await res.json();
                setQuickResponse(data.quick_response || '');
                setSuggestions(data.suggestions || []);
                
                // Add AI response to history
                setConversationHistory(prev => [...prev, { 
                    role: 'assistant', 
                    text: data.quick_response,
                    suggestions: data.suggestions 
                }]);
                
                // Update context with new data
                if (data.priority_score) {
                    setContext(prev => ({
                        ...prev,
                        priority_score: data.priority_score,
                        lane_history: data.lane_history,
                        broker_history: data.broker_history
                    }));
                }
            } else {
                toast.error('Failed to get assistance');
            }
        } catch (e) {
            console.error('Failed to send message:', e);
            toast.error('Connection error');
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };
    
    // Handle quick action
    const handleQuickAction = (input) => {
        sendMessage(input);
    };
    
    // Handle form submit
    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(userInput);
    };
    
    if (loading) {
        return (
            <Card className="bg-zinc-900/80 border-zinc-700 shadow-xl">
                <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
                </CardContent>
            </Card>
        );
    }
    
    const qr = context?.quick_reference || {};
    const loadInfo = context?.load_info || {};
    const priorityScore = context?.priority_score || {};
    const laneHistory = context?.lane_history || {};
    const brokerHistory = context?.broker_history;
    
    return (
        <Card className="bg-zinc-900/95 border-zinc-700 shadow-2xl" data-testid="call-assist-panel">
            {/* Header */}
            <CardHeader className="pb-3 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <PhoneCall className="w-5 h-5 text-green-400" />
                        Call Assist
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/50">LIVE</Badge>
                    </CardTitle>
                    {onClose && (
                        <Button size="sm" variant="ghost" onClick={onClose} className="text-zinc-400">
                            Close
                        </Button>
                    )}
                </div>
                <p className="text-xs text-zinc-500">
                    {loadInfo.origin} → {loadInfo.destination} • {loadInfo.broker_name}
                </p>
            </CardHeader>
            
            <CardContent className="p-4 space-y-4">
                {/* Quick Reference Numbers */}
                <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-green-500/10 rounded-lg p-2 border border-green-500/30">
                        <p className="text-green-400 font-bold text-lg">{qr.opening_ask}</p>
                        <p className="text-xs text-zinc-500">Ask</p>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-2 border border-blue-500/30">
                        <p className="text-blue-400 font-bold text-lg">{qr.target}</p>
                        <p className="text-xs text-zinc-500">Target</p>
                    </div>
                    <div className="bg-amber-500/10 rounded-lg p-2 border border-amber-500/30">
                        <p className="text-amber-400 font-bold text-lg">{qr.floor}</p>
                        <p className="text-xs text-zinc-500">Floor</p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-2 border border-red-500/30">
                        <p className="text-red-400 font-bold text-lg">{qr.green_line}</p>
                        <p className="text-xs text-zinc-500">Min</p>
                    </div>
                </div>
                
                {/* Priority Score & Context */}
                <div className="flex items-center gap-3 text-sm">
                    <Badge className={`${
                        priorityScore.priority === 'PRIORITY' ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' :
                        priorityScore.priority === 'CONSIDER' ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' :
                        'bg-red-500/20 text-red-400 border-red-500/50'
                    }`}>
                        <Brain className="w-3 h-3 mr-1" />
                        Score: {priorityScore.total_score || 0}/100
                    </Badge>
                    
                    {laneHistory.has_history && (
                        <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                            <History className="w-3 h-3 mr-1" />
                            Lane: {laneHistory.success_rate}% success
                        </Badge>
                    )}
                    
                    {brokerHistory && (
                        <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                            <Building2 className="w-3 h-3 mr-1" />
                            {brokerHistory.total_loads_completed || 0} loads
                        </Badge>
                    )}
                </div>
                
                {/* Broker Warning */}
                {context?.broker_warning && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <span className="text-red-300 text-sm">High-risk broker - verify payment terms</span>
                    </div>
                )}
                
                {/* Quick Response - Main AI Response */}
                {quickResponse && (
                    <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                                <Zap className="w-5 h-5 text-cyan-400" />
                                <p className="text-cyan-100 font-medium">{quickResponse}</p>
                            </div>
                            <CopyButton text={quickResponse} />
                        </div>
                    </div>
                )}
                
                {/* Suggestions */}
                {suggestions.length > 0 && (
                    <div className="space-y-2">
                        {suggestions.map((suggestion, idx) => (
                            <SuggestionCard key={idx} suggestion={suggestion} />
                        ))}
                    </div>
                )}
                
                {/* Quick Action Buttons */}
                <QuickActions onAction={handleQuickAction} loading={sending} />
                
                {/* Input Form */}
                <form onSubmit={handleSubmit} className="flex gap-2">
                    <Input
                        ref={inputRef}
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        placeholder="Type what broker said... (e.g., 'They offered $2,500')"
                        className="bg-zinc-800 border-zinc-700 flex-1"
                        disabled={sending}
                        data-testid="call-assist-input"
                    />
                    <Button 
                        type="submit" 
                        disabled={sending || !userInput.trim()}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid="call-assist-send"
                    >
                        {sending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                    </Button>
                </form>
                
                {/* Conversation History (Last 3) */}
                {conversationHistory.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-zinc-800">
                        <p className="text-xs text-zinc-500">Recent:</p>
                        {conversationHistory.slice(-3).map((msg, idx) => (
                            <div key={idx} className={`text-xs ${
                                msg.role === 'user' ? 'text-zinc-400' : 'text-cyan-400'
                            }`}>
                                {msg.role === 'user' ? '→ ' : '← '}
                                {msg.text}
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Opening Script */}
                {context?.opening_script && !quickResponse && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 text-sm">
                        <p className="text-zinc-400 text-xs mb-1">Opening script:</p>
                        <p className="text-zinc-200">{context.opening_script}</p>
                        <CopyButton text={context.opening_script} />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export { SuggestionCard, QuickActions, CopyButton };
